import { removeUserSession } from "../utils/common";
import "../css/Login.css";
import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NewContext } from "../utils/ContextProvider";
import { postUserLogin } from "../utils/peticionesApi";
import Swal from "sweetalert2";
import ModalResetPassword from "../components/ModalResetPassword";
import PortalLoginLayout from "../components/PortalLoginLayout";
import PortalLoginFields from "../components/PortalLoginFields";
import LoginHeroFeatures from "../components/LoginHeroFeatures";
import {
  AVISO_ENTRA_POR_PORTAL, RUTA_LOGIN_PORTAL_LEGALES, esRolExterno, limpiarSesionesParaElPortal,
} from "./admin/legales/legalesAlcance";

const LoginPage = () => {
  const { controlToken, validateLoginToken, setStates, getAlert } = useContext(NewContext);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(true);
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);

  const notify = ({ msj, tipe = "warning" }) => {
    if (typeof getAlert === "function") {
      getAlert({ msj, tipe });
    } else {
      Swal.fire({
        icon: tipe === "succes" ? "success" : tipe === "error" ? "error" : "warning",
        title: msj,
      });
    }
  };

  const handleLogin = async () => {
    setError(null);
    const user = (username || "").trim();
    const pass = (password || "").trim();

    if (!user && !pass) return notify({ msj: "⚠️ Ingresá tu usuario y contraseña." });
    if (!user) return notify({ msj: "⚠️ El usuario es obligatorio." });
    if (!pass) return notify({ msj: "⚠️ La contraseña es obligatoria." });

    setStates?.();
    setLoading(true);

    try {
      const response = await postUserLogin({ password: pass, username: user, company: "" });

      if (response?.status === 200) {
        const token = response.data;

        const result = validateLoginToken(token);

        if (result.type === "multi-company") {
          sessionStorage.setItem("companies", JSON.stringify(result.companies));
          sessionStorage.setItem("temp_username", user);
          // TODO: reemplazar por un token temporal del backend para evitar almacenar credenciales
          sessionStorage.setItem("temp_password", btoa(pass));
          setLoading(false);
          navigate("/select-company");
          return;
        }

        if (result.type === "error") {
          setLoading(false);
          notify({ msj: result.message, tipe: "error" });
          return;
        }

        // El abogado externo no usa la app (fuera de /api/legales su token recibe 403): no se guarda su
        // access_token y va al login del portal de legales con un aviso. Antes se borra cualquier sesión que haya
        // quedado en la pestaña (de la app o del portal), para que el portal no entre con la de otra persona.
        if (esRolExterno(result.payload)) {
          limpiarSesionesParaElPortal();
          setLoading(false);
          setAuthLoading(false);
          navigate(RUTA_LOGIN_PORTAL_LEGALES, { state: { aviso: AVISO_ENTRA_POR_PORTAL, email: user } });
          return;
        }

        // Tampoco queda la sesión del portal de quien usó la pestaña antes (legalesAlcance.js).
        limpiarSesionesParaElPortal();
        sessionStorage.setItem("username", user);
        sessionStorage.setItem("access_token", token);
        await controlToken?.(token);
        setLoading(false);
        setAuthLoading(false);
        navigate("/home/");
        return;
      }

      setLoading(false);
      setAuthLoading(false);
      notify({
        msj: response?.data?.message || "❌ No se pudo iniciar sesión. Verificá tus datos.",
        tipe: "warning",
      });
    } catch (error) {
      const status = error?.response?.status;
      const backendMsg = error?.response?.data?.message;

      let msj = "❌ Ocurrió un error. Intentelo nuevamente.";
      let tipe = "warning";

      if (status === 400 || status === 401) {
        msj = backendMsg || "❌ Usuario y/o contraseña inválidos";
        tipe = "error";
      } else if (status === 403) {
        msj = backendMsg || "⛔ No tenés permisos para acceder.";
        tipe = "warning";
      } else if (status === 429) {
        msj = backendMsg || "⏳ Demasiados intentos. Probá más tarde.";
        tipe = "warning";
      } else if (status >= 500) {
        msj = backendMsg || "💥 Error del servidor. Intenta nuevamente en unos minutos.";
        tipe = "error";
      }

      setLoading(false);
      removeUserSession?.();
      setAuthLoading(false);
      notify({ msj, tipe });
    }
  };

  return (
    <PortalLoginLayout
      claim={
        <>
          Plataforma Integral de Gestión
          <br />
          para Empresas de <strong>Seguridad Privada</strong>
        </>
      }
      heroExtra={<LoginHeroFeatures />}
      longClaim
      title="¡Hola!"
      subtitle="Te damos la bienvenida."
    >
      <div className="login-form">
        <PortalLoginFields
          userLabel="Usuario"
          userType="text"
          value={username}
          onValue={setUsername}
          password={password}
          onPassword={setPassword}
          onEnter={() => !loading && handleLogin()}
        />

        {error && <small className="login-error">{error}</small>}

        <button
          type="button"
          className="login-submit"
          onClick={() => !loading && handleLogin()}
          disabled={loading}
        >
          {loading ? "Cargando…" : "Ingresar"}
        </button>

        <button type="button" className="login-forgot" onClick={() => setShowReset(true)}>
          Olvidé mi contraseña
        </button>
      </div>

      <ModalResetPassword
        show={showReset}
        onClose={() => setShowReset(false)}
        initialEmail={username}
      />
    </PortalLoginLayout>
  );
};

export default LoginPage;
